import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Button, Collapse, Grid, IconButton, styled, Theme } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import moment from 'moment';
import { CompaniesByFirm, CompanyMeasurementDate, MeasurementDateRolloverReturn } from 'api';
import { companiesAction } from 'common/actions';
import { FIX_BEFORE_CONTINUE, NEW_MEASUREMENT_DATE } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { CompanyInfo } from 'common/types/company';
import { Fund } from 'common/types/fund';
import { UseStoreValues } from 'common/types/store';
import { Dialog } from 'components/Dialogs';
import { MDContext } from 'context/MDContext';
import { CompanyIdMeasurementDateOptionsMap, CompanyItem, FundItem, MDContextType } from 'context/MDContext/types';
import { COMPLETE, FAILED } from 'hooks/useRollingApiRequest/useRollingApiRequest.props';
import {
  FUND_INVESTMENTS_COMPANIES_TITLE,
  FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE,
} from 'pages/FundInvestments/common/constants';
import { useGetCompaniesFromPreviousMD } from 'services/hooks/firms/useGetCompaniesFromPreviousMD/useGetCompaniesFromPreviousMD';
import { useGetCompanyListByFirmId } from 'services/hooks/firms/useGetCompanyListByFirmId/useGetCompanyListByFirmId';
import { CompanyRollover } from 'services/hooks/useRolloverCompanies/rollover.types';
import useRolloverCompanies from 'services/hooks/useRolloverCompanies/useRolloverCompanies';
import { getObjectValue, MOMENT_DEFAULT_DATE_FORMAT, objToArray } from 'utilities';
import { FIRST, SECOND, THIRD } from './MeasurementDateDialog.constants';
import CompaniesTable from '../components/companies-table/CompaniesTable';
import CompaniesSelector from '../components/CompaniesSelector';
import { MeasurementDateLabel } from '../types';
import { MeasurementDateDialogProps } from './MeasurementDateDialog.props';
import RolloverStatus from '../components/RolloverStatus/components/rollover-status/RolloverStatus';
import { NONE } from '../constants';

// Styled components
const StyledActionButtonsGrid = styled(Grid)(({ theme }: { theme: Theme }) => ({
  marginTop: theme.spacing(2),
}));

const StyledDangerButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  color: theme.palette.error.contrastText,
  backgroundColor: theme.palette.error.light,
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
  },
}));

export const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paperWidthSm': {
    maxWidth: '700px !important',
  },
}));

const MeasurementDateDialog = (props: MeasurementDateDialogProps) => {
  const { open, onClose, activeFund, activeCompany, getMeasurementDates, getFundMeasurementDates } = props;

  const [{ firmInfo, fundList }, dispatch] = useStore() as UseStoreValues;
  const { are_fund_investments_enabled: areFundInvestmentsEnabled = false } = getObjectValue(firmInfo);

  const [measurementDate, setMeasurementDate] = useState<string | undefined>();
  const [items, setItems] = useState<FundItem[]>([]);
  const [selectedFunds, setSelectedFunds] = useState<{ [key: string]: boolean }>({});
  const [selectedCompanies, setSelectedCompanies] = useState<{ [key: number]: boolean }>({});
  const [rolloverDetailCompanies, setRolloverDetailCompanies] = useState<CompanyRollover[]>([]);
  const [activeStep, setActiveStep] = useState(FIRST);
  const [rollover, setRollover] = useState(false);
  const [shouldResetFinancials, setShouldResetFinancials] = useState(false);
  const [shouldExcludeDocuments, setShouldExcludeDocuments] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const subItems = useMemo(() => items.flatMap(item => item.subItems), [items]);

  const { companies, getCompanies, isLoading: loadingCompanies } = useGetCompaniesFromPreviousMD();
  const { getCompanies: updateCompanies } = useGetCompanyListByFirmId();

  const selectedFundNames = useMemo(
    () => fundList?.filter(fund => selectedFunds[fund.id as number]).map(fund => fund.name as string) ?? [],
    [fundList, selectedFunds]
  );

  // each company
  const sortedMeasurementDateOptionsByCompanyId = useMemo(
    () =>
      companies?.reduce((acc, company) => {
        const options = company.measurement_dates
          .map(date => moment(date))
          .sort((a, b) => b.diff(a))
          .map(date => date.format(MOMENT_DEFAULT_DATE_FORMAT));
        options.unshift(NONE);
        acc[company.company_id] = options;
        return acc;
      }, {} as CompanyIdMeasurementDateOptionsMap),
    [companies]
  );

  const companyIdMap = useMemo(
    () =>
      companies?.reduce(
        (acc, company) => {
          acc[company.company_id] = company;
          return acc;
        },
        {} as { [key: number]: CompaniesByFirm }
      ),
    [companies]
  );

  const latestPossibleMarketDate = useMemo(() => moment().subtract(1, 'days'), []);

  const marketDate = useMemo(() => {
    const md = moment(measurementDate);
    return md.isAfter(latestPossibleMarketDate, 'day')
      ? latestPossibleMarketDate.format(MOMENT_DEFAULT_DATE_FORMAT)
      : md.format(MOMENT_DEFAULT_DATE_FORMAT);
  }, [measurementDate, latestPossibleMarketDate]);

  const companiesWithoutFundTitle = useMemo(
    () =>
      `${
        areFundInvestmentsEnabled ? FUND_INVESTMENTS_INDEXES_ETFS_COMPANIES_TITLE : FUND_INVESTMENTS_COMPANIES_TITLE
      } without Fund`,
    [areFundInvestmentsEnabled]
  );

  useEffect(() => {
    const rolloverDetails = Object.entries(selectedCompanies)
      .filter(([, isSelected]) => isSelected)
      .reduce((acc, [companyId]) => {
        const company = companyIdMap[Number(companyId)];

        if (company) {
          const sortedMeasurementDates = sortedMeasurementDateOptionsByCompanyId[company.company_id];
          //  If there is only none in the array, use that, else use the most recent (second record)
          const mdDate = sortedMeasurementDates?.length === 1 ? sortedMeasurementDates[0] : sortedMeasurementDates[1];
          acc.push({
            companyId: company.company_id,
            companyName: company.company_name,
            customDate: mdDate === NONE ? mdDate : moment(mdDate).format(MOMENT_DEFAULT_DATE_FORMAT),
            excludeDocuments: false,
            excludeValuation: false,
            isFundInvestment: company.company_type === CompaniesByFirm.company_type.INVESTMENT,
            keepMarketData: false,
            marketDate: marketDate || null,
            resetFinancials: false,
          });
        }
        return acc;
      }, [] as CompanyRollover[]);
    setRolloverDetailCompanies(rolloverDetails);
  }, [selectedCompanies, companyIdMap, sortedMeasurementDateOptionsByCompanyId, marketDate]);

  useEffect(() => {
    if (activeFund) {
      setSelectedFunds({ [activeFund.id as number]: true });
    }
  }, [activeFund]);

  useEffect(() => {
    if (activeCompany) {
      setSelectedCompanies({ [activeCompany.id as number]: true });
    }
  }, [activeCompany]);

  const getSelectedFunds = useCallback(
    () =>
      items.reduce((funds, fund) => {
        let isSelected;
        if (fund.id) {
          if (fund.subItems.length === 0 || fund.subItems.every(company => !selectedCompanies?.[company.id])) {
            isSelected = selectedFunds?.[fund.id];
          } else {
            isSelected = fund.subItems.every(
              company => selectedCompanies?.[company.id] && fund.id && !funds.includes(fund.id)
            );
          }
          if (isSelected) {
            funds.push(fund.id);
          }
        }
        return funds;
      }, [] as number[]),
    [items, selectedCompanies, selectedFunds]
  );

  // we can go directly to the submit step if every company selected has no measurement dates
  const submit = useMemo(
    () =>
      selectedCompanies
      && companies
      && objToArray<string, boolean>(selectedCompanies)
        .filter(([, selected]) => selected)
        .map(([companyId]) => companies.find(company => company.company_id.toString() === companyId))
        .every(company => !company?.measurement_dates?.length),
    [selectedCompanies, companies]
  );

  const isStep = useCallback((step: number) => activeStep === step, [activeStep]);

  const handleBack = useCallback(() => {
    setActiveStep(lastStep => lastStep - 1);
  }, []);

  const handleCreate = useCallback(() => {
    setActiveStep(THIRD);
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep(lastStep => lastStep + 1);
  }, []);

  const { launchRequests, processingRollover, result } = useRolloverCompanies({
    firmInfo,
    measurementDate,
    selectedFunds: getSelectedFunds(),
    rolloverDetailCompanies,
  });

  const disableActionButtons = useMemo(
    () =>
      !measurementDate
      || isUndefined(companies)
      || (isEmpty(selectedFunds) && isEmpty(selectedCompanies))
      || processingRollover,
    [measurementDate, companies, selectedFunds, selectedCompanies, processingRollover]
  );

  const handleSubmit = useCallback(async () => {
    launchRequests();
  }, [launchRequests]);

  const onCloseLedgerUpdate = useCallback(async () => {
    onClose();
    if (!firmInfo) {
      return;
    }
    const updatedCompanies = await updateCompanies(firmInfo.id as number);
    if (activeCompany) {
      const updatedCompany = updatedCompanies.find(company => company.company_id === activeCompany.id);
      if (updatedCompany) {
        dispatch(companiesAction.setCompanyInfo({ ...updatedCompany }));
      }
    }
    if (getFundMeasurementDates) {
      await getFundMeasurementDates(
        result.fundResult.filter(r => r.result).map(r => r.result as MeasurementDateRolloverReturn)
      );
    }
    if (getMeasurementDates && activeCompany?.id) {
      const companyResults = result.companyResult.map(r => r.result);
      const createdMds = [...companyResults].filter(r => r) as CompanyMeasurementDate[];
      const companyCreatedMD = createdMds.find(md => md.company === activeCompany.id);
      if (companyCreatedMD) {
        await getMeasurementDates(companyCreatedMD);
      }
    }
  }, [
    onClose,
    firmInfo,
    updateCompanies,
    activeCompany,
    getFundMeasurementDates,
    getMeasurementDates,
    dispatch,
    result,
  ]);

  useEffect(() => {
    if (isStep(THIRD)) {
      handleSubmit();
    }
  }, [isStep, handleSubmit]);

  const getContent = () => {
    const steps = [
      <CompaniesSelector key={FIRST} activeCompany={activeCompany as CompanyInfo} />,
      <CompaniesTable key={SECOND} />,
      <RolloverStatus key={THIRD} />,
    ];

    return steps[activeStep];
  };

  useEffect(() => {
    if (firmInfo?.id) {
      getCompanies(firmInfo.id);
    }
  }, [firmInfo, getCompanies]);

  useEffect(() => {
    if (companies) {
      let tmpItems: FundItem[] = [];

      const companiesWithoutFund = { id: null, name: companiesWithoutFundTitle };

      if (!isEmpty(companies) && !isEmpty(fundList)) {
        const funds: (Fund | MeasurementDateLabel)[] = [companiesWithoutFund, ...(fundList as Fund[])];

        tmpItems = funds.reduce((filteredFunds: FundItem[], fund) => {
          const companiesSubItems = companies.reduce((filteredCompanies: CompanyItem[], item) => {
            if (item) {
              if (fund.id === item.fund_id) {
                filteredCompanies.push({
                  id: item.company_id,
                  name: item.company_name,
                  status: item.status,
                });
              }
            }

            return filteredCompanies;
          }, []);

          filteredFunds.push({
            id: fund.id,
            name: fund.name as string,
            subItems: companiesSubItems,
          });

          return filteredFunds;
        }, []);
      }

      if (isEmpty(companies) && !isEmpty(activeCompany)) {
        tmpItems = [
          ...tmpItems,
          {
            ...companiesWithoutFund,
            subItems: [
              {
                id: activeCompany.id as number,
                name: activeCompany.name,
              },
            ],
          },
        ];
      }

      setItems(tmpItems);
    }
  }, [companies, fundList, activeFund, activeCompany, companiesWithoutFundTitle]);

  const requestStatus = useMemo(
    () => [...result.fundResult, ...result.companyResult, ...result.companyResult].map(({ state }) => state),
    [result]
  );

  const progress = useMemo(() => {
    const completedCount = requestStatus.filter(status => status === COMPLETE || status === FAILED).length;
    const totalCount = requestStatus.length;
    if (totalCount === 0) {
      return 0;
    }
    return Math.round((completedCount / totalCount) * 100);
  }, [requestStatus]);

  const mdContextValue = useMemo<MDContextType>(
    () => ({
      errors,
      items,
      subItems,
      selectedFundNames,
      measurementDate,
      selectedFunds,
      selectedCompanies,
      rollover,
      setMeasurementDate,
      setSelectedFunds,
      setSelectedCompanies,
      measurementDateOptionsByCompanyId: sortedMeasurementDateOptionsByCompanyId,
      rolloverDetailCompanies,
      setRolloverDetailCompanies,
      setRollover,
      setShouldResetFinancials,
      shouldResetFinancials,
      shouldExcludeDocuments,
      setShouldExcludeDocuments,
      latestPossibleMarketDate,
      marketDate,
      statusList: result,
      isLoading: loadingCompanies || processingRollover,
    }),
    [
      selectedFundNames,
      subItems,
      result,
      errors,
      items,
      loadingCompanies,
      measurementDate,
      sortedMeasurementDateOptionsByCompanyId,
      processingRollover,
      rollover,
      latestPossibleMarketDate,
      marketDate,
      selectedCompanies,
      selectedFunds,
      setShouldResetFinancials,
      shouldExcludeDocuments,
      setShouldExcludeDocuments,
      shouldResetFinancials,
      rolloverDetailCompanies,
    ]
  );

  return (
    <StyledDialog
      open={open}
      maxWidth="lg"
      title={NEW_MEASUREMENT_DATE}
      onClose={onClose}
      loadingIncrement={progress}
      isLoading={loadingCompanies || processingRollover}
      fullWidth
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        disableTitleClose: false,
      }}>
      <MDContext.Provider value={mdContextValue}>
        {!isEmpty(errors) && (
          <Collapse in={!isEmpty(errors)}>
            <Alert
              variant="outlined"
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setErrors({});
                  }}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }>
              {FIX_BEFORE_CONTINUE}
            </Alert>
          </Collapse>
        )}
        {getContent()}
        <StyledActionButtonsGrid container justifyContent="flex-end" spacing={2}>
          {isStep(FIRST) && (
            <Grid item>
              <Button
                id={submit ? 'create-md-btn' : 'next-md-btn'}
                variant="contained"
                color="secondary"
                onClick={() => (submit ? handleCreate() : handleNext())}
                disabled={disableActionButtons}>
                {submit ? 'Create' : 'Next'}
              </Button>
            </Grid>
          )}
          {isStep(SECOND) && (
            <Grid item>
              <Button color="primary" onClick={handleBack}>
                Back
              </Button>
            </Grid>
          )}
          {isStep(SECOND) && (
            <Grid item>
              <Button
                id="rollover-ok-btn"
                color="secondary"
                onClick={handleNext}
                variant="contained"
                disabled={processingRollover || isUndefined(rollover)}>
                Create
              </Button>
            </Grid>
          )}
          {isStep(THIRD) && (
            <Grid item>
              <Button
                id="rollover-ok-btn"
                color="secondary"
                onClick={onCloseLedgerUpdate}
                disabled={processingRollover}
                variant="contained">
                Ok
              </Button>
            </Grid>
          )}
        </StyledActionButtonsGrid>
      </MDContext.Provider>
    </StyledDialog>
  );
};

export default MeasurementDateDialog;
